import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import { FaPills, FaBoxes, FaExclamationTriangle, FaFileInvoice } from "react-icons/fa";
import { StatCard } from "../components";
import "../styles/common.css";
import "./DashboardPharmacie.css";

export default function DashboardPharmacie() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalProduits: 0,
    totalStocks: 0,
    stockFaible: 0,
    expiresBientot: 0,
    totalFactures: 0,
    montantTotalAchats: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      // Charger toutes les données nécessaires
      const [produitsRes, stocksRes, stockFaibleRes, expiresRes, facturesRes] = await Promise.all([
        api.get("/api/produits-pharmaceutiques/?actif=true"),
        api.get("/api/stocks-pharmacie/"),
        api.get("/api/stocks-pharmacie/stock-faible/?seuil=10"),
        api.get("/api/stocks-pharmacie/expires-bientot/?jours=30"),
        api.get("/api/factures/"),
      ]);

      const totalFactures = facturesRes.data.length;
      const montantTotal = facturesRes.data.reduce((sum, facture) => {
        const montant = parseFloat(facture.montant_total) || 0;
        return sum + montant;
      }, 0);

      // Debug: Afficher les données de stock pour comprendre la structure (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log("Données de stock reçues:", stocksRes.data);
        if (stocksRes.data.length > 0) {
          console.log("Premier élément de stock:", stocksRes.data[0]);
        }
      }

      // Calculer la somme totale des quantités disponibles dans les stocks
      const totalStockQuantite = stocksRes.data.reduce((sum, stock) => {
        // Le champ peut être "quantite", "quantite_disponible", ou "stock_disponible"
        const quantite = stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0;
        return sum + (parseFloat(quantite) || 0);
      }, 0);

      setStats({
        totalProduits: produitsRes.data.length,
        totalStocks: totalStockQuantite, // Afficher la somme des quantités au lieu du nombre d'entrées
        stockFaible: stockFaibleRes.data.length,
        expiresBientot: expiresRes.data.length,
        totalFactures,
        montantTotalAchats: montantTotal,
      });
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error("Erreur Dashboard Pharmacie:", err);
      setError("Impossible de charger les statistiques de la pharmacie.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const onRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    // S'assurer que le montant est un nombre valide
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(numAmount).replace('XOF', 'FCFA');
  };

  if (loading) {
    return (
      <div className="dashboard-pharmacie-page">
        <div className="loading-container">
          <p className="loading-text">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-pharmacie-page">
        <div className="title-badge">
          <FaPills className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button
            onClick={onRetry}
            className="retry-button"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-pharmacie-page">
      <div className="title-badge">
        <FaPills className="page-title-icon" aria-hidden="true" />
        <span>{t.pharmacyDashboard}</span>
      </div>

      <div className="dashboard-pharmacie-container">
        {/* Section des indicateurs clés (KPI) */}
        <h2 className="dashboard-section-title">{t.overview}</h2>
        <div className="kpi-grid">
          <StatCard 
            type="produits" 
            label={t.activeProducts} 
            value={stats.totalProduits} 
            variant="pharmacie"
            icon={<FaPills />}
            actionButton={{
              label: t.see,
              onClick: () => navigate("/produits-pharmaceutiques")
            }}
          />
          <StatCard 
            type="stocks" 
            label={t.availableStocks} 
            value={stats.totalStocks} 
            variant="pharmacie"
            icon={<FaBoxes />}
            actionButton={{
              label: t.see,
              onClick: () => navigate("/stocks-pharmacie")
            }}
          />
          <StatCard 
            type="stock-faible" 
            label={t.lowStock} 
            value={stats.stockFaible} 
            variant="pharmacie"
            icon={<FaExclamationTriangle />}
            actionButton={{
              label: t.see,
              onClick: () => navigate("/stocks-pharmacie")
            }}
          />
          <StatCard 
            type="expires" 
            label={t.expiringSoon} 
            value={stats.expiresBientot} 
            variant="pharmacie"
            icon={<FaExclamationTriangle />}
            actionButton={{
              label: t.see,
              onClick: () => navigate("/stocks-pharmacie")
            }}
          />
          <StatCard 
            type="factures" 
            label={t.totalInvoices} 
            value={stats.totalFactures} 
            variant="pharmacie"
            icon={<FaFileInvoice />}
            actionButton={{
              label: t.see,
              onClick: () => navigate("/factures")
            }}
          />
          <StatCard 
            type="montant" 
            label={t.totalAmount} 
            value={formatCurrency(stats.montantTotalAchats)} 
            variant="pharmacie"
            icon={<FaFileInvoice />}
          />
        </div>

        {/* Actions rapides */}
        <h2 className="dashboard-section-title">{t.quickActions}</h2>
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-produit-pharmaceutique")}
          >
            <FaPills />
            <span>{t.addProduct}</span>
          </button>
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-facture")}
          >
            <FaFileInvoice />
            <span>{t.addInvoice}</span>
          </button>
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/utilisations-pharmacie")}
          >
            <FaBoxes />
            <span>{t.viewUsages}</span>
          </button>
        </div>
      </div>
    </div>
  );
}